export type RecamanOperation = "add" | "subtract";

export interface WorldTerm {
  n: string;
  previous: string;
  value: string;
  step: string;
  operation: RecamanOperation;
}

export interface WorldSnapshot {
  worldId: string;
  serverTimeMs: number;
  startedAtMs: number;
  tickMs: number;
  currentN: string;
  current: WorldTerm;
  generatedThrough: string;
  catchingUp: boolean;
  nextTickAtMs: number;
}

export interface WorldBatch {
  generatedThrough: string;
  serverTimeMs: number;
  catchingUp: boolean;
  terms: WorldTerm[];
}

export type ConnectionState = "connecting" | "live" | "reconnecting" | "offline";

export interface WorldView {
  snapshot: WorldSnapshot;
  terms: WorldTerm[];
  connection: ConnectionState;
  lastSyncMs: number | null;
  usingFallback: boolean;
}

export interface TermWindowMerge {
  terms: WorldTerm[];
  hasForwardGap: boolean;
}

export interface FrozenWorldDisplay {
  term: WorldTerm;
  terms: WorldTerm[];
}

const DECIMAL_PATTERN = /^(0|[1-9][0-9]*)$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readDecimal(value: unknown, field: string): string {
  if (typeof value !== "string" || !DECIMAL_PATTERN.test(value)) {
    throw new TypeError(`${field} must be a canonical decimal string`);
  }
  return value;
}

function readFiniteNumber(value: unknown, field: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${field} must be a finite number`);
  }
  return value;
}

export function parseWorldTerm(value: unknown): WorldTerm {
  if (!isRecord(value)) throw new TypeError("term must be an object");
  const operation = value.operation;
  if (operation !== "add" && operation !== "subtract") {
    throw new TypeError("operation must be add or subtract");
  }
  return {
    n: readDecimal(value.n, "term.n"),
    previous: readDecimal(value.previous, "term.previous"),
    value: readDecimal(value.value, "term.value"),
    step: readDecimal(value.step, "term.step"),
    operation,
  };
}

export function parseWorldSnapshot(value: unknown): WorldSnapshot {
  if (!isRecord(value)) throw new TypeError("snapshot must be an object");
  if (typeof value.worldId !== "string" || value.worldId.length === 0) {
    throw new TypeError("worldId must be a non-empty string");
  }
  return {
    worldId: value.worldId,
    serverTimeMs: readFiniteNumber(value.serverTimeMs, "serverTimeMs"),
    startedAtMs: readFiniteNumber(value.startedAtMs, "startedAtMs"),
    tickMs: readFiniteNumber(value.tickMs, "tickMs"),
    currentN: readDecimal(value.currentN, "currentN"),
    current: parseWorldTerm(value.current),
    generatedThrough: readDecimal(value.generatedThrough, "generatedThrough"),
    catchingUp: Boolean(value.catchingUp),
    nextTickAtMs: readFiniteNumber(value.nextTickAtMs, "nextTickAtMs"),
  };
}

export function parseTermsPayload(value: unknown): WorldTerm[] {
  if (!isRecord(value) || !Array.isArray(value.terms)) {
    throw new TypeError("terms response must contain an array");
  }
  return value.terms.map(parseWorldTerm);
}

export function parseWorldBatch(value: unknown): WorldBatch {
  if (!isRecord(value) || !Array.isArray(value.terms)) {
    throw new TypeError("batch must contain a terms array");
  }
  return {
    generatedThrough: readDecimal(value.generatedThrough, "generatedThrough"),
    serverTimeMs: readFiniteNumber(value.serverTimeMs, "serverTimeMs"),
    catchingUp: value.catchingUp === true,
    terms: value.terms.map(parseWorldTerm),
  };
}

export function compareDecimal(left: string, right: string): number {
  if (left.length !== right.length) return left.length - right.length;
  return left === right ? 0 : left < right ? -1 : 1;
}

function readPositiveSafeInteger(value: number, field: string): number {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${field} must be a positive safe integer`);
  }
  return value;
}

export function mergeTermWindows(
  existing: readonly WorldTerm[],
  incoming: readonly WorldTerm[],
  maximum: number,
): TermWindowMerge {
  const limit = readPositiveSafeInteger(maximum, "maximum");
  const existingLast = existing.at(-1);
  const incomingFirst = incoming[0];
  const hasForwardGap =
    existingLast !== undefined &&
    incomingFirst !== undefined &&
    BigInt(incomingFirst.n) > BigInt(existingLast.n) + 1n;

  const byN = new Map<string, WorldTerm>();
  for (const term of existing) byN.set(term.n, term);
  for (const term of incoming) byN.set(term.n, term);
  const sorted = [...byN.values()].sort((left, right) => compareDecimal(left.n, right.n));

  return {
    terms: sorted.length > limit ? sorted.slice(-limit) : sorted,
    hasForwardGap,
  };
}

export function logicalNAt(
  startedAtMs: number,
  tickMs: number,
  serverNowMs: number,
  generatedThrough: string,
): bigint {
  if (![startedAtMs, tickMs, serverNowMs].every(Number.isFinite) || tickMs <= 0) {
    throw new RangeError("world clock values must be finite and tickMs must be positive");
  }
  const generated = BigInt(readDecimal(generatedThrough, "generatedThrough"));
  const elapsed = Math.max(0, serverNowMs - startedAtMs);
  const logical = BigInt(Math.floor(elapsed / tickMs));
  return logical > generated ? generated : logical;
}

export function termAtOrBefore(terms: readonly WorldTerm[], n: bigint): WorldTerm | undefined {
  let low = 0;
  let high = terms.length - 1;
  let result: WorldTerm | undefined;
  while (low <= high) {
    const mid = (low + high) >>> 1;
    const candidate = terms[mid];
    if (!candidate) break;
    const candidateN = BigInt(candidate.n);
    if (candidateN <= n) {
      result = candidate;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return result;
}

export function termWindowNeedsRecovery(
  window: TermWindowMerge,
  currentN: string,
): boolean {
  const current = BigInt(readDecimal(currentN, "currentN"));
  return window.hasForwardGap || termAtOrBefore(window.terms, current) === undefined;
}

export function selectTermWindowAtOrBefore(
  terms: readonly WorldTerm[],
  through: bigint,
  maximum: number,
): WorldTerm[] {
  const limit = readPositiveSafeInteger(maximum, "maximum");
  let low = 0;
  let high = terms.length - 1;
  let endExclusive = 0;

  while (low <= high) {
    const mid = (low + high) >>> 1;
    const candidate = terms[mid];
    if (!candidate) break;
    if (BigInt(candidate.n) <= through) {
      endExclusive = mid + 1;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return terms.slice(Math.max(0, endExclusive - limit), endExclusive);
}

export function freezeWorldDisplay(
  term: WorldTerm,
  visibleTerms: readonly WorldTerm[],
  maximum: number,
): FrozenWorldDisplay {
  const limit = readPositiveSafeInteger(maximum, "maximum");
  const through = BigInt(term.n);
  const selected = selectTermWindowAtOrBefore(visibleTerms, through, limit);
  const byN = new Map(selected.map((candidate) => [candidate.n, candidate]));
  byN.set(term.n, term);
  const terms = [...byN.values()].sort((left, right) => compareDecimal(left.n, right.n));

  return {
    term,
    terms: terms.length > limit ? terms.slice(-limit) : terms,
  };
}

export function serverOffsetAtRequestMidpoint(
  serverTimeMs: number,
  requestStartedAtMs: number,
  responseReceivedAtMs: number,
): number {
  if (![serverTimeMs, requestStartedAtMs, responseReceivedAtMs].every(Number.isFinite)) {
    throw new RangeError("clock samples must be finite");
  }
  if (responseReceivedAtMs < requestStartedAtMs) {
    throw new RangeError("response time must not precede request time");
  }
  const midpoint = requestStartedAtMs + (responseReceivedAtMs - requestStartedAtMs) / 2;
  return serverTimeMs - midpoint;
}
